import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import Groups from './pages/Groups';
import Modules from './pages/Modules';
import Reports from './pages/Reports';
import Profile from './pages/Profile';
import Permissions from './pages/Permissions';
import AgentCallHistory from './pages/AgentCallHistory';
import AgentPerformanceReport from './pages/AgentPerformanceReport';
import AgentCallCountReport from './pages/AgentCallCountReport';
import AgentCallListReport from './pages/AgentCallListReport';
import AllContacts from './pages/AllContacts';
import UploadContactsCsv from './pages/UploadContactsCsv';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import ProtectedRoute from './components/ProtectedRoute';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Component to handle default route redirection
const DefaultRoute: React.FC = () => {
  const { user, token } = useAuth();
  
  if (token && user) {
    return <Navigate to="/dashboard" replace />;
  } else {
    return <Navigate to="/login" replace />;
  }
};

// Component to prevent authenticated users from accessing login
const LoginRoute: React.FC = () => {
  const { user, token } = useAuth();
  
  if (token && user) {
    return <Navigate to="/dashboard" replace />;
  } else {
    return <Login />;
  }
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route path="/login" element={<LoginRoute />} />
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/users" 
        element={
          <ProtectedRoute requiredPermission="users">
            <Users />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/groups" 
        element={
          <ProtectedRoute requiredPermission="groups">
            <Groups />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/modules" 
        element={
          <ProtectedRoute requiredPermission="modules">
            <Modules />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/permissions" 
        element={
          <ProtectedRoute>
            <Permissions />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/permissions/:type/:id" 
        element={
          <ProtectedRoute>
            <Permissions />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/reports" 
        element={
          <ProtectedRoute>
            <Reports />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/profile" 
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/agent/:agentId/calls" 
        element={
          <ProtectedRoute>
            <AgentCallHistory />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/agent-performance-report" 
        element={
          <ProtectedRoute>
            <AgentPerformanceReport />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/agent-report" 
        element={
          <ProtectedRoute>
            <AgentPerformanceReport />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/agent-call-count-report" 
        element={
          <ProtectedRoute>
            <AgentCallCountReport />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/call-count-report" 
        element={
          <ProtectedRoute>
            <AgentCallCountReport />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/agent-call-list-report" 
        element={
          <ProtectedRoute>
            <AgentCallListReport />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/call-list-report" 
        element={
          <ProtectedRoute>
            <AgentCallListReport />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/all-contacts" 
        element={
          <ProtectedRoute>
            <AllContacts />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/upload-contacts-csv" 
        element={
          <ProtectedRoute>
            <UploadContactsCsv />
          </ProtectedRoute>
        } 
      />
      <Route path="/" element={<DefaultRoute />} />
      <Route path="*" element={<DefaultRoute />} />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <div className="App">
            <AppRoutes />
            <ToastContainer
              position="top-right"
              autoClose={5000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="light"
            />
          </div>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
